/**
 * Orchestra AI Platform - Execution Panel Component
 * 
 * Panel for executing workflows and viewing execution results
 * 
 * @fileoverview Execution panel with start/stop, progress, and logs
 */

import { useState } from 'react';
import PropTypes from 'prop-types';
import { useWorkflowStore } from '../../store/workflowStore';
import { topologicalSort, collectNodeInputs } from '../../utils/workflowUtils';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import './ExecutionPanel.css';

/**
 * @typedef {Object} ExecutionResult
 * @property {boolean} success
 * @property {Object} results
 * @property {number} executionTime
 * @property {string[]} [errors]
 */

/**
 * Execute API call
 * @param {Object} nodeData - Node data
 * @param {Object} inputs - Input data
 * @returns {Promise<Object>} API result
 */
async function executeAPICall(nodeData, inputs) {
  const { data, error } = await supabase.functions.invoke('ai-proxy', {
    body: {
      server: nodeData.server,
      endpoint: nodeData.endpoint,
      method: nodeData.method || 'POST',
      data: inputs,
    },
  });

  if (error) throw error;
  return data.data || data;
}

/**
 * Execution Panel Component
 * @param {Object} props
 * @param {string} props.workflowId
 */
export function ExecutionPanel({ workflowId }) {
  const { nodes, edges, isExecuting, setExecuting, setExecutionResults, updateNode } =
    useWorkflowStore();
  const [logs, setLogs] = useState([]);
  const [executionTime, setExecutionTime] = useState(0);

  /**
   * Execute workflow
   * @param {Object} inputData - Initial input data
   */
  const executeWorkflow = async (inputData = {}) => {
    if (nodes.length === 0) {
      toast.error('Workflow has no nodes');
      return;
    }

    setExecuting(true);
    setLogs([]);
    setExecutionTime(0);
    const startTime = Date.now();

    try {
      // Build execution order
      const executionOrder = topologicalSort(nodes, edges);

      // Execute nodes in order
      const results = { input: inputData };

      for (const nodeId of executionOrder) {
        const node = nodes.find((n) => n.id === nodeId);
        if (!node) continue;

        // Update node status to running
        updateNode(nodeId, {
          data: { ...node.data, status: 'running' },
        });

        setLogs((prev) => [
          ...prev,
          { type: 'info', message: `Executing ${node.data.label || nodeId}...` },
        ]);

        try {
          // Collect inputs
          const inputs = collectNodeInputs(node, edges, results);

          // Execute API call (only for API nodes)
          if (node.type === 'apiNode') {
            const result = await executeAPICall(node.data, inputs);
            results[nodeId] = result;

            // Update node status to success
            updateNode(nodeId, {
              data: { ...node.data, status: 'success', lastResult: result },
            });

            setLogs((prev) => [
              ...prev,
              { type: 'success', message: `${node.data.label || nodeId} completed successfully` },
            ]);
          } else {
            // For non-API nodes, just pass through
            results[nodeId] = inputs;
            updateNode(nodeId, {
              data: { ...node.data, status: 'success' },
            });
          }
        } catch (error) {
          // Update node status to error
          updateNode(nodeId, {
            data: { ...node.data, status: 'error', error: error.message },
          });

          setLogs((prev) => [
            ...prev,
            { type: 'error', message: `${node.data.label || nodeId} failed: ${error.message}` },
          ]);

          throw error;
        }
      }

      const totalTime = Date.now() - startTime;
      setExecutionTime(totalTime);

      setExecutionResults({
        success: true,
        results,
        executionTime: totalTime,
      });

      toast.success('Workflow executed successfully');
    } catch (error) {
      const totalTime = Date.now() - startTime;
      setExecutionTime(totalTime);

      setExecutionResults({
        success: false,
        error: error.message,
        executionTime: totalTime,
      });

      toast.error('Workflow execution failed: ' + error.message);
    } finally {
      setExecuting(false);
    }
  };

  /**
   * Stop execution
   */
  const stopExecution = () => {
    setExecuting(false);
    toast('Execution stopped', { icon: '⏹️' });
  };

  /**
   * Clear logs
   */
  const clearLogs = () => {
    setLogs([]);
  };

  return (
    <div className="execution-panel">
      <div className="execution-panel-header">
        <div className="execution-panel-header-content">
          <h2 className="execution-panel-title">Execution</h2>
          <div className="execution-panel-actions">
            {isExecuting ? (
              <button
                onClick={stopExecution}
                className="execution-panel-button execution-panel-button-stop"
              >
                Stop
              </button>
            ) : (
              <button
                onClick={() => executeWorkflow({})}
                className="execution-panel-button execution-panel-button-run"
              >
                Run Workflow
              </button>
            )}
            <button
              onClick={clearLogs}
              className="execution-panel-button execution-panel-button-clear"
            >
              Clear Logs
            </button>
          </div>
        </div>

        {executionTime > 0 && (
          <div className="execution-panel-time">
            Execution time: {executionTime}ms
          </div>
        )}
      </div>

      {/* Logs */}
      <div className="execution-panel-logs">
        {logs.length === 0 ? (
          <p className="execution-panel-logs-empty">No execution logs yet</p>
        ) : (
          <div className="execution-panel-logs-list">
            {logs.map((log, index) => (
              <div
                key={index}
                className={`execution-panel-log-item ${
                  log.type === 'error'
                    ? 'execution-panel-log-item-error'
                    : log.type === 'success'
                    ? 'execution-panel-log-item-success'
                    : 'execution-panel-log-item-info'
                }`}
              >
                {log.message}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

ExecutionPanel.propTypes = {
  workflowId: PropTypes.string.isRequired,
};

